'use strict'

// Single-point entry for cross-document communication.
// Sends & receives postMessage to/from the parent window
class HostCom {

  constructor() {
    window.addEventListener('message', (e) => { this.receive(e) }, false)

    // callbacks registry. Key is action (or action action + message id for response handlers); value is an array of handlers (function or object)
    this._callbacks = {}
    this._messageQueue = {} // store events received without handler. Each action has its own queue

    // origin window info
    this._targetWindow = window.parent
    this._targetOrigin = document.referrer || '*' // referrer is sometimes empty (e.g. safari in teads context). Fallback to catch-all value.
  }

  // Sends a postMessage to the host window.
  // @action: (string) message action type
  // @data: (object) payload to send
  // @callback: (function) executed when target responds
  send(action, data = {}, callback = null) {
    if (typeof data === 'function') {
      callback = data
      data = {}
    }

    data.action = action
    data._iid = window.name

    // register callback
    if (typeof callback === 'function') {
      data._mid = Utils.uuid() // adds message id

      // store handler with the message id in key
      this._callbacks[action + data._mid] = [{ cb: callback, unbind: true }]
    }

    this._targetWindow.postMessage(data, this._targetOrigin)
  }

  // register a callback for an event
  // action: (string) event name
  // fn: (function) callback to execute
  on(action, fn) {
    this._callbacks[action] = this._callbacks[action] || []
    this._callbacks[action].push(fn)
    this._checkQueue(action) // looks for events already received and waiting for their handler to register
  }

  // registers a one-time callback for an event. Once executed, the listener is deleted.
  // action: (string) event name
  // fn: (function) callback to execute
  one(action, fn) {
    this._callbacks[action] = this._callbacks[action] || []
    this._callbacks[action].push({
      cb: fn,
      unbind: true
    })
    this._checkQueue(action)
  }

  // removes handler for event.
  // action: (string) event name
  // fn: (function) callback to remove
  off(action, fn) {
    // the same handler can be referenced for different actions (if it's a response handler, it uses a random id).
    // So we loop all handler lists to make sure we don't miss any.
    Object.entries(this._callbacks).forEach(([key, cbs]) => {
      if (!key.startsWith(action)) return

      const idx = cbs.findIndex(x => x === fn || x.cb === fn)
      if (idx > -1)
        cbs.splice(idx, 1)
    })
  }

  // Listener for message events. Executes the associated callback if there's one in the registered callbacks,
  // or store its data for later processing
  receive(evt) {
    if (!evt || !evt.data)
      return

    let key = evt.data.action
    if (evt.data._mid)
      key += evt.data._mid

    if (this._callbacks[key])
      this._processEvent(key, evt.data)
    else{
      // no handler for event, store it in appropriate queue.
      // A message with no handler cannot be an answer to send message, so no _mid set and no need to compound the key
      if (!this._messageQueue[evt.data.action])
        this._messageQueue[evt.data.action] = []

      this._messageQueue[evt.data.action].push(evt.data)
    }
  }

  // executes callback associated with event data
  // @action: (string) action to identify the handler to run. Either the event action or action + message id (if message is an awser to a send request)
  // @data: (object) message payload
  _processEvent(action, data) {
    const unbindIndices = [] // list of indices to remove from handlers list

    try{
      // loop all handlers registered for the action
      this._callbacks[action].forEach((handler, i) => {
        let fn, unbind = false

        if (typeof handler === 'function')
          fn = handler
        else {
          fn = handler.cb
          unbind = handler.unbind
        }

        fn.call(null, data)

        // store index of handler to remove
        // when an item is removed, array is modified & indices are shifted. Take that into account when setting index to remove
        if (unbind)
          unbindIndices.push(i - unbindIndices.length)
      })
    }
    catch(ex) {
      console.error(ex)
    }

    // remove handlers marked for unbinding
    for (let i = 0; i < unbindIndices.length; i++) {
      this._callbacks[action].splice(unbindIndices[i], 1)
    }
  }

  // checks queue for waiting messages
  _checkQueue(action) {
    if (!this._messageQueue[action] || this._messageQueue[action].length === 0)
      return

    // process each waiting event
    // message in queues cannot come as anwser to send message, so no _mid, key is action
    this._messageQueue[action].forEach(msg => { this._processEvent(action, msg) })
    delete this._messageQueue[action] // clear queue when every event has been processed.
  }

  // set target origin to the catch-all '*' value. This is necessary when communicating with a host page on the file: protocol.
  allowLaxTarget() {
    this._targetOrigin = '*'
  }

  // Returns whether there's any listener registered for the given event name.
  hasKey(key) {
    return !!(this._callbacks[key] && this._callbacks[key].length > 0)
  }
}

window.hostCom = new HostCom()
